/**
 * useMapEditorState Hook
 *
 * Composes the drawing, editing, and undo/redo hooks into a unified
 * map editor state management interface. Used by the GymMapEditor component.
 *
 * Features:
 * - Unified interface for all editor operations
 * - Drawing new polygon regions
 * - Editing existing regions (vertex manipulation)
 * - Undo/redo support
 * - Auto-save with debouncing
 *
 * @example
 * ```tsx
 * const {
 *   regions,
 *   isDrawing,
 *   editingRegionName,
 *   startDrawing,
 *   finishDrawing,
 *   selectRegionForEditing,
 *   undo,
 *   redo,
 * } = useMapEditorState({
 *   floorPlan,
 *   onSave,
 * });
 * ```
 */

import { useState, useCallback, useEffect } from 'react';
import { useDrawingState, type FinishedPolygon } from './useDrawingState';
import { useEditingState } from './useEditingState';
import { useUndoRedo, type EditorAction } from './useUndoRedo';
import { findOverlappingRegion } from '@/utils/geometry';
import type { Point, WallSectionRegion, GymFloorPlan } from '@flash/shared';

/**
 * Options for useMapEditorState hook
 */
export interface UseMapEditorStateOptions {
  /** Current floor plan with regions */
  floorPlan: GymFloorPlan;
  /** Callback to save updated regions */
  onSave: (regions: WallSectionRegion[]) => Promise<void>;
  /** Debounce delay for auto-save in ms (default: 1000) */
  autoSaveDelay?: number;
}

/**
 * Return value from useMapEditorState hook
 */
export interface UseMapEditorStateResult {
  // === Region Data ===
  /** Current list of all regions */
  regions: WallSectionRegion[];

  // === Drawing State ===
  /** Whether currently in drawing mode */
  isDrawing: boolean;
  /** Points of the polygon currently being drawn */
  currentPolygon: Point[];
  /** Wall section currently being drawn for */
  selectedWallSection: string | null;
  /** Whether current polygon can be finished (>= 3 points) */
  canFinishDrawing: boolean;
  /** Start drawing a polygon for a wall section */
  startDrawing: (wallSectionName: string) => void;
  /** Add a point to the current polygon */
  addPoint: (point: Point) => void;
  /** Remove the last point from current polygon */
  removeLastPoint: () => void;
  /** Finish drawing and save the new region. Returns error if region overlaps. */
  finishDrawing: () => Promise<{ success: boolean; error?: string }>;
  /** Cancel drawing without saving */
  cancelDrawing: () => void;

  // === Editing State ===
  /** Name of the region currently being edited */
  editingRegionName: string | null;
  /** Full data of the region being edited */
  editingRegion: WallSectionRegion | null;
  /** Index of the active vertex being manipulated */
  activeVertexIndex: number | null;
  /** Whether a vertex is currently being dragged */
  isDraggingVertex: boolean;
  /** Whether the editing region can have a vertex deleted */
  canDeleteVertex: boolean;
  /** Select a region to edit */
  selectRegionForEditing: (wallSectionName: string) => void;
  /** Clear editing selection */
  clearEditing: () => void;
  /** Start dragging a vertex */
  startDraggingVertex: (vertexIndex: number) => void;
  /** Stop dragging the current vertex */
  stopDraggingVertex: () => void;
  /** Update a vertex position */
  updateVertexPosition: (vertexIndex: number, newPosition: Point) => Promise<void>;
  /** Update the entire polygon (for batch updates) */
  updatePolygon: (newPolygon: Point[]) => Promise<void>;
  /** Delete a vertex from the editing region */
  deleteVertex: (vertexIndex: number) => Promise<void>;
  /** Add a vertex to the editing region */
  addVertex: (afterIndex: number, point: Point) => Promise<void>;
  /** Delete the currently editing region */
  deleteRegion: (regionName: string) => Promise<void>;
  /** Rename a region */
  renameRegion: (oldName: string, newName: string) => Promise<void>;

  // === Undo/Redo ===
  /** Whether there are actions that can be undone */
  canUndo: boolean;
  /** Whether there are actions that can be redone */
  canRedo: boolean;
  /** Undo the last action */
  undo: () => Promise<void>;
  /** Redo the last undone action */
  redo: () => Promise<void>;

  // === Saving State ===
  /** Whether currently saving to server */
  isSaving: boolean;
  /** Last save error, if any */
  saveError: string | null;
  /** Clear the save error */
  clearSaveError: () => void;
}

/**
 * Hook for managing complete map editor state
 */
export const useMapEditorState = ({
  floorPlan,
  onSave,
}: UseMapEditorStateOptions): UseMapEditorStateResult => {
  // Local copy of regions that we can modify
  const [regions, setRegions] = useState<WallSectionRegion[]>(floorPlan.regions);

  // Saving state
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Sync regions when floorPlan prop changes
  useEffect(() => {
    setRegions(floorPlan.regions);
  }, [floorPlan.regions]);

  // Compose the individual state hooks
  const drawingState = useDrawingState();
  const editingState = useEditingState(regions);
  const undoRedoState = useUndoRedo(regions);

  /**
   * Save regions to server
   */
  const saveRegions = useCallback(
    async (newRegions: WallSectionRegion[]) => {
      setIsSaving(true);
      setSaveError(null);
      try {
        await onSave(newRegions);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to save';
        setSaveError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [onSave]
  );

  /**
   * Apply action, update regions, and save
   */
  const applyActionAndSave = useCallback(
    async (action: EditorAction, newRegions: WallSectionRegion[]) => {
      undoRedoState.pushAction(action);
      setRegions(newRegions);
      await saveRegions(newRegions);
    },
    [undoRedoState, saveRegions]
  );

  // === Drawing Operations ===

  const finishDrawing = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    // Check for overlaps before finishing
    const currentPolygon = drawingState.currentPolygon;
    if (currentPolygon.length >= 3) {
      const overlappingRegion = findOverlappingRegion(currentPolygon, regions);
      if (overlappingRegion) {
        // Cancel drawing and return error
        drawingState.cancelDrawing();
        return {
          success: false,
          error: `Region overlaps with "${overlappingRegion}". Regions can touch but cannot overlap.`,
        };
      }
    }

    const finished: FinishedPolygon | null = drawingState.finishDrawing();
    if (!finished) return { success: false };

    const newRegion: WallSectionRegion = {
      wallSectionName: finished.wallSectionName,
      polygon: finished.polygon,
    };

    const newRegions = [...regions, newRegion];

    const action: EditorAction = {
      type: 'ADD_REGION',
      regionName: finished.wallSectionName,
      newPolygon: finished.polygon,
    };

    await applyActionAndSave(action, newRegions);
    return { success: true };
  }, [drawingState, regions, applyActionAndSave]);

  // === Editing Operations ===

  const updateVertexPosition = useCallback(
    async (vertexIndex: number, newPosition: Point) => {
      if (!editingState.editingRegion) return;

      const previousPolygon = editingState.editingRegion.polygon;
      const updatedPolygon = editingState.updateVertexPosition(vertexIndex, newPosition);
      if (!updatedPolygon) return;

      const newRegions = regions.map((r) =>
        r.wallSectionName === editingState.editingRegionName ? { ...r, polygon: updatedPolygon } : r
      );

      const action: EditorAction = {
        type: 'UPDATE_REGION',
        regionName: editingState.editingRegionName!,
        previousPolygon,
        newPolygon: updatedPolygon,
      };

      await applyActionAndSave(action, newRegions);
    },
    [editingState, regions, applyActionAndSave]
  );

  const updatePolygon = useCallback(
    async (newPolygon: Point[]) => {
      if (!editingState.editingRegion) return;

      const previousPolygon = editingState.editingRegion.polygon;

      const newRegions = regions.map((r) =>
        r.wallSectionName === editingState.editingRegionName ? { ...r, polygon: newPolygon } : r
      );

      const action: EditorAction = {
        type: 'UPDATE_REGION',
        regionName: editingState.editingRegionName!,
        previousPolygon,
        newPolygon,
      };

      await applyActionAndSave(action, newRegions);
    },
    [editingState, regions, applyActionAndSave]
  );

  const deleteVertex = useCallback(
    async (vertexIndex: number) => {
      if (!editingState.editingRegion) return;

      const previousPolygon = editingState.editingRegion.polygon;
      const updatedPolygon = editingState.deleteVertex(vertexIndex);
      if (!updatedPolygon) return;

      const newRegions = regions.map((r) =>
        r.wallSectionName === editingState.editingRegionName ? { ...r, polygon: updatedPolygon } : r
      );

      const action: EditorAction = {
        type: 'UPDATE_REGION',
        regionName: editingState.editingRegionName!,
        previousPolygon,
        newPolygon: updatedPolygon,
      };

      await applyActionAndSave(action, newRegions);
    },
    [editingState, regions, applyActionAndSave]
  );

  const addVertex = useCallback(
    async (afterIndex: number, point: Point) => {
      if (!editingState.editingRegion) return;

      const previousPolygon = editingState.editingRegion.polygon;
      const updatedPolygon = editingState.addVertex(afterIndex, point);
      if (!updatedPolygon) return;

      const newRegions = regions.map((r) =>
        r.wallSectionName === editingState.editingRegionName ? { ...r, polygon: updatedPolygon } : r
      );

      const action: EditorAction = {
        type: 'UPDATE_REGION',
        regionName: editingState.editingRegionName!,
        previousPolygon,
        newPolygon: updatedPolygon,
      };

      await applyActionAndSave(action, newRegions);
    },
    [editingState, regions, applyActionAndSave]
  );

  const deleteRegion = useCallback(
    async (regionName: string) => {
      const region = regions.find((r) => r.wallSectionName === regionName);
      if (!region) return;

      const newRegions = regions.filter((r) => r.wallSectionName !== regionName);

      const action: EditorAction = {
        type: 'DELETE_REGION',
        regionName,
        previousPolygon: region.polygon,
      };

      // Clear editing if we're deleting the editing region
      if (editingState.editingRegionName === regionName) {
        editingState.clearEditing();
      }

      await applyActionAndSave(action, newRegions);
    },
    [regions, editingState, applyActionAndSave]
  );

  const renameRegion = useCallback(
    async (oldName: string, newName: string) => {
      const region = regions.find((r) => r.wallSectionName === oldName);
      if (!region) return;

      // Check for duplicate name (case-insensitive)
      const trimmedNewName = newName.trim();
      if (
        regions.some(
          (r) =>
            r.wallSectionName !== oldName &&
            r.wallSectionName.toLowerCase() === trimmedNewName.toLowerCase()
        )
      ) {
        throw new Error(`A region named "${trimmedNewName}" already exists`);
      }

      const newRegions = regions.map((r) =>
        r.wallSectionName === oldName ? { ...r, wallSectionName: trimmedNewName } : r
      );

      // Clear editing if we're renaming the editing region
      if (editingState.editingRegionName === oldName) {
        editingState.clearEditing();
      }

      // Save without adding to undo stack (rename is a simple operation)
      setRegions(newRegions);
      await saveRegions(newRegions);
    },
    [regions, editingState, saveRegions]
  );

  // === Undo/Redo Operations ===

  const undo = useCallback(async () => {
    const newRegions = undoRedoState.undo(regions);
    if (!newRegions) return;

    setRegions(newRegions);
    await saveRegions(newRegions);
  }, [undoRedoState, regions, saveRegions]);

  const redo = useCallback(async () => {
    const newRegions = undoRedoState.redo(regions);
    if (!newRegions) return;

    setRegions(newRegions);
    await saveRegions(newRegions);
  }, [undoRedoState, regions, saveRegions]);

  // === Error Handling ===

  const clearSaveError = useCallback(() => {
    setSaveError(null);
  }, []);

  return {
    // Region Data
    regions,

    // Drawing State
    isDrawing: drawingState.isDrawing,
    currentPolygon: drawingState.currentPolygon,
    selectedWallSection: drawingState.selectedWallSection,
    canFinishDrawing: drawingState.canFinishDrawing,
    startDrawing: drawingState.startDrawing,
    addPoint: drawingState.addPoint,
    removeLastPoint: drawingState.removeLastPoint,
    finishDrawing,
    cancelDrawing: drawingState.cancelDrawing,

    // Editing State
    editingRegionName: editingState.editingRegionName,
    editingRegion: editingState.editingRegion,
    activeVertexIndex: editingState.activeVertexIndex,
    isDraggingVertex: editingState.isDraggingVertex,
    canDeleteVertex: editingState.canDeleteVertex,
    selectRegionForEditing: editingState.selectRegionForEditing,
    clearEditing: editingState.clearEditing,
    startDraggingVertex: editingState.startDraggingVertex,
    stopDraggingVertex: editingState.stopDraggingVertex,
    updateVertexPosition,
    updatePolygon,
    deleteVertex,
    addVertex,
    deleteRegion,
    renameRegion,

    // Undo/Redo
    canUndo: undoRedoState.canUndo,
    canRedo: undoRedoState.canRedo,
    undo,
    redo,

    // Saving State
    isSaving,
    saveError,
    clearSaveError,
  };
};

export default useMapEditorState;
